import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import LoadingAnimation from '../components/LoadingAnimation';
import { SETUP_GENERATION_KEY } from '../components/onboarding/accountSetup';
import { LOADING_CAROUSEL_IMAGES } from '../components/loading/loadingCarousel';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import {
  generacionPendiente,
  limpiarGeneracionPendiente,
  recuperarGeneracionPendiente,
} from '../lib/primeraGeneracion';
import { preloadGeneratedImages } from '../utils/preloadGeneratedImages';

/**
 * Pantalla de "generación" de la primera imagen (Figma node 71:4037, ADR-724).
 * Es la pantalla de loading que ve el usuario tras tocar "Generar imagen" en el
 * paso 4.
 *
 * El diseño **ya no vive acá**: es el de `LoadingAnimation`, el mismo componente que
 * usa toda la app para cualquier generación (ADR-2026-08-02-pantalla-carga-unificada).
 * Esta página se quedó sólo con lo suyo: el guard de sesión, el timeline según el
 * origen, y a dónde navegar cuando termina.
 *
 * **Timeline según el origen (ADR-743, barra del upload en
 * ADR-2026-08-03-onboarding-barra-con-progreso):**
 * - **Ejemplo precargado** (cartera/camisa): tiempo **fijo** de 10s. La barra arranca
 *   en **0% y se queda ahí 2s** (`EXAMPLE_HOLD_MS`, "preparando") y recién después se
 *   llena 0→100% en los 8s restantes (`completeAtEnd`); al llegar avanza a resultados.
 * - **Imagen propia (upload):** generación **real**
 *   (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real). La barra avanza **paceada** a
 *   `UPLOAD_DURATION_MS` con tope de 97% y salta a 100% recién cuando la generación
 *   terminó de verdad (`completeNow`) — igual que el resto de la app.
 */

// ── Timeline del ejemplo precargado (determinista) ──
const EXAMPLE_HOLD_MS = 2000; // barra en 0% (preparando), antes de "generar"
const EXAMPLE_FILL_MS = 8000; // barra 0→100% (generando)

/**
 * Paceo de la barra en el camino de **foto propia**. No es una medición: es la
 * estimación con la que avanza la barra falsa, igual que en toda la app (el backend no
 * reporta porcentaje). Sale de los 80s que usa `InterfazV2` para imágenes, más el
 * trabajo extra que sólo tiene este flujo antes y después de generar: crear producto y
 * variante, subir la foto full + thumbnail, y precargar las URLs firmadas.
 *
 * Quedarse corto no rompe nada — la barra espera en 97% hasta que termine de verdad —
 * así que ante la duda conviene estimar de más y no de menos.
 */
const UPLOAD_DURATION_MS = 90_000;

export const AccountSetupGenerating = () => {
  const navigate = useNavigate();

  // Origen de la generación: ejemplo precargado (id conocido) vs imagen propia ('upload').
  // Cualquier cosa que no sea explícitamente 'upload' se trata como ejemplo (default demo).
  const [generationKey] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SETUP_GENERATION_KEY);
    } catch {
      return null;
    }
  });
  // Sólo un upload explícito usa el camino de generación real; cualquier otro valor
  // (id de ejemplo, o null si se entró directo) usa el timeline fijo del ejemplo.
  const isExample = generationKey !== 'upload';

  // La generación real terminó → la barra salta a 100%. En el ejemplo nunca se levanta:
  // ahí la barra completa sola al cumplirse el tiempo (`completeAtEnd`).
  const [listo, setListo] = useState(false);
  // Barra en 100% → fade-out; recién al terminarlo se navega, así la pantalla no corta
  // de golpe.
  const [saliendo, setSaliendo] = useState(false);

  // A dónde ir cuando termina el fade. El ejemplo va derecho a la demo (default); el
  // upload lo reemplaza por uno que **lleva las imágenes ya generadas**. Va en un ref y
  // no en estado porque es un callback, no algo que deba re-renderizar.
  const salidaRef = useRef<(() => void) | null>(null);
  const alTerminarSalida = useCallback(() => {
    if (salidaRef.current) salidaRef.current();
    // `replace`: esta pantalla de carga sale del historial al pasar a resultados, para que
    // el gesto de "volver" no devuelva a generar. Ver ADR-2026-08-14-onboarding-back-no-regenera.
    else navigate(ONBOARDING_V2.setupResultados, { replace: true });
  }, [navigate]);

  // Necesita sesión (viene post-login). Si no hay, vuelve a la bienvenida.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && !data.session) navigate(ONBOARDING_V2.bienvenida, { replace: true });
    });
    return () => {
      active = false;
    };
  }, [navigate]);

  // Imagen propia: generación REAL (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real).
  // La pantalla dura lo que tarde de verdad — por eso la barra es indeterminada. La
  // foto la dejó el paso 4 en memoria; si no está (refresh o deep-link acá) no hay
  // nada que generar y se vuelve al paso 4.
  useEffect(() => {
    if (isExample) return;
    // Idempotente: con StrictMode este efecto corre dos veces y las dos obtienen la
    // MISMA promesa, así que se genera (y se cobra) una sola vez.
    // Tras un refresh no hay promesa en memoria, pero puede haber una generación
    // EN VUELO en el backend (marcador en localStorage): se re-engancha por poll en
    // vez de rebotar al paso 4 — un reintento ahí crearía otro producto y cobraría
    // créditos de nuevo mientras el primer resultado se pierde en el historial.
    const promesa = generacionPendiente() ?? recuperarGeneracionPendiente();
    if (!promesa) {
      navigate(ONBOARDING_V2.setupProducto, { replace: true });
      return;
    }
    let activo = true;
    (async () => {
      try {
        const res = await promesa;
        // Se precargan las URLs firmadas ANTES de navegar: la pantalla de resultados
        // muestra las imágenes al instante, sin el parpadeo de cargarlas ahí.
        const preload = await preloadGeneratedImages(res.imageIds, res.companyId).catch(
          () => [] as Awaited<ReturnType<typeof preloadGeneratedImages>>,
        );
        if (!activo) return;
        const resultados = preload.map((img, i) => ({
          label: `Opción ${i + 1}`,
          src: img.optSignedUrl || img.signedUrl,
          full: img.signedUrl,
        }));
        // Ya se usó: el traspaso se cierra para que un próximo intento arranque limpio.
        limpiarGeneracionPendiente();
        // Todo listo (incluido el precargado): se deja armada la navegación y se avisa a
        // la barra. Ella completa a 100%, dispara `onCompleted` → fade → `onExitComplete`.
        // `replace` (ídem rama del ejemplo): al pasar a resultados, la carga sale del
        // historial para que el back del celular no vuelva a generar.
        salidaRef.current = () =>
          navigate(ONBOARDING_V2.setupResultados, {
            replace: true,
            state: { resultadosReales: resultados },
          });
        setListo(true);
      } catch (err) {
        // Se limpia SIEMPRE, aunque este efecto ya no esté activo: si no, la promesa
        // rechazada quedaría cacheada y el reintento devolvería el mismo error sin
        // volver a generar.
        limpiarGeneracionPendiente();
        if (!activo) return;
        // Un fallo acá puede haber consumido créditos o no; el mensaje del backend
        // (cuando es accionable) es el que mejor lo explica. Vuelve al paso 4 con el
        // motivo, en vez de dejar la pantalla girando para siempre. Sin fade: es un
        // error, no vale la pena demorar la vuelta.
        navigate(ONBOARDING_V2.setupProducto, {
          replace: true,
          state: {
            errorGeneracion:
              err instanceof Error && err.message
                ? err.message
                : 'No pudimos generar las imágenes. Probá de nuevo.',
          },
        });
      }
    })();
    return () => {
      activo = false;
    };
  }, [isExample, navigate]);

  return (
    <LoadingAnimation
      variant="onboarding"
      message="Estamos generando tu primera imagen"
      subMessage="Solo tomará unos segundos"
      carouselTitle="Esto es lo que podés crear con Atlas"
      // Explícito: el alta va con fotos SIEMPRE, sin depender de la cuenta de
      // generaciones (ADR-2026-08-04-fotos-en-las-primeras-generaciones). Igual es la
      // generación nº1, así que suma al contador.
      carouselImages={LOADING_CAROUSEL_IMAGES}
      // Las dos ramas usan barra con progreso; cambia sólo quién la lleva a 100%.
      // Ejemplo: timeline fijo, completa sola al cumplirse el tiempo.
      // Upload: paceada con tope de 97%, completa cuando la generación terminó de verdad.
      holdMs={isExample ? EXAMPLE_HOLD_MS : 0}
      durationMs={isExample ? EXAMPLE_FILL_MS : UPLOAD_DURATION_MS}
      completeAtEnd={isExample}
      completeNow={listo}
      onCompleted={() => setSaliendo(true)}
      exiting={saliendo}
      onExitComplete={alTerminarSalida}
    />
  );
};

export default AccountSetupGenerating;
